import { Color3, type Material, Matrix, type Mesh, MeshBuilder, Quaternion, type Scene, TransformNode, Vector3 } from "@babylonjs/core";
import { heroDef, type HeroId, STATUS_BITS } from "@rivalwatch/shared";
import { CLIENT_HEROES } from "../heroes/index.ts";
import type { AvatarFrame, AvatarRig, RigPart } from "../heroes/types.ts";
import { glow, toon } from "./materials.ts";
import { instantiateHero, type ModelInstance } from "./models.ts";

const DARK = "#565d6e";
const SKIN = "#e9c7a8";

export interface Avatar {
  root: TransformNode;
  /** Upper body pivot: pitches with aim. */
  torso: TransformNode;
  legL: TransformNode;
  legR: TransformNode;
  armL: TransformNode;
  meshes: Mesh[];
  worldMeshes: Mesh[];
  hero: HeroId;
  form: number;
  walkPhase: number;
  model?: ModelInstance;
  anim?: string;
  rig: AvatarRig;
  /** Whatever the hero's gear() returned; handed to its animate() each frame. */
  gear: unknown;
  /** Pitches the model's upper body with aim. */
  aim?: TransformNode;
  /** Seconds since built. */
  age: number;
  dispose(): void;
}

function chainWorld(n: TransformNode) {
  const chain: TransformNode[] = [];
  for (let p: TransformNode | null = n; p; p = p.parent as TransformNode | null) chain.unshift(p);
  for (const c of chain) c.computeWorldMatrix(true);
  return n.getWorldMatrix();
}

/** A node under `parent` whose frame is the avatar root's at rest: metric, hero-facing, but carried by the parent's animation. */
function socket(scene: Scene, name: string, parent: TransformNode, local: Vector3) {
  const pw = chainWorld(parent);
  const w = Vector3.TransformCoordinates(local, pw);
  const m = Matrix.Translation(w.x, w.y, w.z).multiply(Matrix.Invert(pw));
  const s = new TransformNode(name, scene);
  const scl = new Vector3();
  const rot = new Quaternion();
  const pos = new Vector3();
  m.decompose(scl, rot, pos);
  s.parent = parent;
  s.scaling.copyFrom(scl);
  s.rotationQuaternion = rot;
  s.position.copyFrom(pos);
  return s;
}

/** The arm's rotation in the first frame of a holding clip (arm raised to aim). */
function heldArm(model: ModelInstance, clip: string, arm: Mesh): Quaternion | undefined {
  const t = model.anims.get(clip)?.targetedAnimations.find((a) => a.target === arm && a.animation.targetProperty === "rotationQuaternion");
  const v = t?.animation.getKeys()[0]?.value;
  return v instanceof Quaternion ? v.clone() : undefined;
}

/** Like socket(), but posed with the arm raised as in the holding clips, so −Z is where the fist aims. */
function handSocket(scene: Scene, name: string, arm: Mesh, local: Vector3, raised: Quaternion | undefined) {
  const bind = arm.rotationQuaternion?.clone();
  const bindEuler = arm.rotation.clone();
  if (raised) arm.rotationQuaternion = raised;
  const s = socket(scene, name, arm, local);
  if (bind) arm.rotationQuaternion = bind;
  else {
    arm.rotationQuaternion = null;
    arm.rotation.copyFrom(bindEuler);
  }
  arm.computeWorldMatrix(true);
  return s;
}

function modelRig(scene: Scene, model: ModelInstance): AvatarRig {
  const p = model.parts;
  const size = {} as AvatarRig["size"];
  const box = (m: Mesh) => m.getBoundingInfo().boundingBox;
  for (const k of Object.keys(p) as RigPart[]) {
    chainWorld(p[k]);
    const b = box(p[k]);
    size[k] = { x: b.maximumWorld.x - b.minimumWorld.x, y: b.maximumWorld.y - b.minimumWorld.y, z: b.maximumWorld.z - b.minimumWorld.z };
  }
  const centre = (m: Mesh) => box(m).center.clone();
  const end = (m: Mesh, f: number) => {
    const b = box(m);
    return new Vector3(b.center.x, b.minimum.y + (b.maximum.y - b.minimum.y) * f, b.center.z);
  };
  return {
    head: socket(scene, "rig-head", p.head, centre(p.head)),
    torso: socket(scene, "rig-torso", p.torso, centre(p.torso)),
    armL: socket(scene, "rig-armL", p.armL, Vector3.Zero()),
    armR: socket(scene, "rig-armR", p.armR, Vector3.Zero()),
    handL: handSocket(scene, "rig-handL", p.armL, end(p.armL, 0.1), heldArm(model, "holding-left", p.armL)),
    handR: handSocket(scene, "rig-handR", p.armR, end(p.armR, 0.1), heldArm(model, "holding-right", p.armR)),
    legL: socket(scene, "rig-legL", p.legL, Vector3.Zero()),
    legR: socket(scene, "rig-legR", p.legR, Vector3.Zero()),
    footL: socket(scene, "rig-footL", p.legL, end(p.legL, 0)),
    footR: socket(scene, "rig-footR", p.legR, end(p.legR, 0)),
    parts: p,
    size,
  };
}

/**
 * Procedural stylized hero models built from primitives (placeholders until the CC0 model
 * pass). Origin at the feet, facing -Z (yaw 0), sized to each hero's collision capsule.
 */
export function buildAvatar(scene: Scene, hero: HeroId, outline: string | null, form = 0): Avatar {
  const def = heroDef(hero, form);
  const ch = CLIENT_HEROES[hero];
  const formVisual = form > 0 ? ch.forms?.[form - 1] : undefined;
  const H = def.height;
  const R = def.radius;
  const root = new TransformNode(`avatar-${hero}`, scene);
  const meshes: Mesh[] = [];
  const main = toon(scene, def.color);
  const dark = toon(scene, DARK);
  const model = instantiateHero(scene, hero, formVisual?.shape ?? ch.shape, form);
  /** Procedural body parts — replaced by the CC0 model when it's loaded. */
  const body: Mesh[] = [];
  const add = (m: Mesh, parent: TransformNode, mat: Material = main) => {
    m.parent = parent;
    m.material = mat;
    meshes.push(m);
    return m;
  };
  const world: Mesh[] = [];
  const addWorld = (m: Mesh, mat?: Material) => {
    if (mat) m.material = mat;
    m.isPickable = false;
    world.push(m);
    return m;
  };

  const hipY = H * 0.45;
  const legs = new TransformNode("legs", scene);
  legs.parent = root;
  const mkLeg = (side: number) => {
    const pivot = new TransformNode("leg", scene);
    pivot.parent = legs;
    pivot.position.set(side * R * 0.45, hipY, 0);
    const leg = add(MeshBuilder.CreateCapsule("leg", { radius: R * 0.28, height: hipY, tessellation: 8, subdivisions: 1 }, scene), pivot, dark);
    body.push(leg);
    leg.position.y = -hipY / 2;
    const boot = add(MeshBuilder.CreateBox("boot", { width: R * 0.55, height: 0.14, depth: R * 0.85 }, scene), pivot, main);
    boot.position.set(0, -hipY + 0.07, -R * 0.12);
    body.push(boot);
    return pivot;
  };
  const legL = mkLeg(-1);
  const legR = mkLeg(1);

  const torso = new TransformNode("torso", scene);
  torso.parent = root;
  torso.position.y = hipY;
  const chestH = H * 0.33;
  const bulk = formVisual?.bulk ?? ch.bulk ?? 1;
  const chest = add(
    MeshBuilder.CreateBox("chest", { width: R * 1.7 * bulk, height: chestH, depth: R * 1.1 * bulk }, scene),
    torso,
  );
  chest.position.y = chestH / 2 + 0.02;
  body.push(chest);
  const belt = add(MeshBuilder.CreateBox("belt", { width: R * 1.6 * bulk, height: 0.1, depth: R * 1.05 * bulk }, scene), torso, dark);
  belt.position.y = 0.02;
  body.push(belt);
  const head = add(MeshBuilder.CreateSphere("head", { diameter: def.headRadius * 2.1, segments: 12 }, scene), torso, toon(scene, SKIN));
  head.position.y = chestH + def.headRadius * 1.05;
  body.push(head);
  const neckTop = chestH;

  // Arms: both reach forward to hold the weapon.
  const armL = new TransformNode("armL", scene);
  armL.parent = torso;
  armL.position.set(-R * 0.95 * bulk, neckTop - 0.08, 0);
  const armR = new TransformNode("armR", scene);
  armR.parent = torso;
  armR.position.set(R * 0.95 * bulk, neckTop - 0.08, 0);
  for (const [arm, side] of [[armL, -1], [armR, 1]] as const) {
    const a = add(MeshBuilder.CreateCapsule("arm", { radius: R * 0.22 * bulk, height: H * 0.32, tessellation: 8, subdivisions: 1 }, scene), arm);
    a.rotation.x = Math.PI / 2.6;
    a.rotation.z = side * 0.25;
    a.position.set(-side * 0.08, -0.12, -H * 0.1);
    body.push(a);
  }
  const hand = new TransformNode("hand", scene);
  hand.parent = torso;
  hand.position.set(R * 0.35, neckTop - 0.28, -H * 0.24);

  let rig: AvatarRig;
  let aim: TransformNode | undefined;
  if (model) {
    for (const m of body) {
      meshes.splice(meshes.indexOf(m), 1);
      m.dispose();
    }
    const mroot = model.root;
    mroot.parent = root;
    const k = H / model.height;
    mroot.scaling.set(k * bulk, k, k * bulk);
    // glTF characters face +Z; our heroes face -Z at yaw 0.
    mroot.rotation.y = Math.PI;
    mroot.rotationQuaternion = null;
    // Clips never translate the torso, so a pivot can sit between it and the root to pitch the upper body with aim.
    const t = model.parts.torso;
    aim = new TransformNode("aim", scene);
    aim.parent = t.parent;
    aim.position.copyFrom(t.position);
    t.parent = aim;
    t.position.setAll(0);
    rig = modelRig(scene, model);
    for (const m of mroot.getChildMeshes()) meshes.push(m as Mesh);
  } else {
    const node = (name: string, parent: TransformNode, x = 0, y = 0, z = 0) => {
      const n = new TransformNode(name, scene);
      n.parent = parent;
      n.position.set(x, y, z);
      return n;
    };
    const d = def.headRadius * 2.1;
    const limb = { x: R * 0.44 * bulk, y: H * 0.32, z: R * 0.44 * bulk };
    rig = {
      head: node("rig-head", head),
      torso: node("rig-torso", torso, 0, chestH / 2, 0),
      armL: node("rig-armL", armL),
      armR: node("rig-armR", armR),
      handL: node("rig-handL", torso, -R * 0.35, neckTop - 0.28, -H * 0.24),
      handR: node("rig-handR", hand),
      legL: node("rig-legL", legL),
      legR: node("rig-legR", legR),
      footL: node("rig-footL", legL, 0, -hipY, 0),
      footR: node("rig-footR", legR, 0, -hipY, 0),
      parts: {},
      size: { head: { x: d, y: d, z: d }, torso: { x: R * 1.7 * bulk, y: chestH, z: R * 1.1 * bulk }, armL: limb, armR: limb, legL: { x: R * 0.56, y: hipY, z: R * 0.56 }, legR: { x: R * 0.56, y: hipY, z: R * 0.56 } },
    };
  }

  const gear = ch.gear({ scene, def, form, add, addWorld, torso, hand, head, armL, armR, legs, rig, main, dark, glow: (hex, a) => glow(scene, hex, a), toon: (hex) => toon(scene, hex), chestH, R, H, bulk });

  if (outline) {
    const oc = Color3.FromHexString(outline);
    for (const m of meshes) {
      if (m.material && (m.material as { disableLighting?: boolean }).disableLighting) continue;
      m.renderOutline = true;
      m.outlineColor = oc;
      m.outlineWidth = 0.025;
    }
  }
  for (const m of meshes) m.isPickable = false;

  return {
    root,
    torso,
    legL,
    legR,
    armL,
    meshes,
    worldMeshes: world,
    hero,
    form,
    walkPhase: 0,
    model,
    rig,
    gear,
    aim,
    age: 0,
    dispose() {
      model?.dispose();
      for (const m of world) m.dispose();
      root.dispose(false, false);
    },
  };
}

/** Procedural animation: leg swing from ground speed, torso pitch from aim, crouch squash. */
function playClip(a: Avatar, name: string, speedRatio = 1) {
  const m = a.model!;
  const g = m.anims.get(name) ?? m.anims.get("idle");
  if (!g) return;
  g.speedRatio = speedRatio;
  if (a.anim === name) return;
  if (a.anim) m.anims.get(a.anim)?.stop();
  a.anim = name;
  g.start(true, speedRatio);
}

/** Pose from movement/action (or the hero's own override), aim pitch, crouch squash, knockdown; then the hero's animate() hook. */
export function animateAvatar(a: Avatar, f: Omit<AvatarFrame, "t">) {
  a.age += f.dt;
  const clip = CLIENT_HEROES[a.hero].animate?.(a.gear, { ...f, t: a.age }, a.rig);
  const down = (f.status & (STATUS_BITS.knockdown | STATUS_BITS.sleep)) !== 0;
  const crouchY = f.crouch ? 0.72 : 1;
  a.root.scaling.x += (1 - a.root.scaling.x) * Math.min(1, f.dt * 10);
  a.root.scaling.z += (1 - a.root.scaling.z) * Math.min(1, f.dt * 10);
  a.root.scaling.y += (crouchY - a.root.scaling.y) * Math.min(1, f.dt * 14);
  a.root.rotation.z = down ? Math.PI / 2.2 : 0;
  a.torso.rotation.x = f.pitch * 0.6;
  if (a.model) {
    // 1 Fire, 2 AltFire, 3 Melee, 4 Cast, 5 Ult (see shared ActionKind)
    if (clip) playClip(a, clip);
    else if (f.action === 3) playClip(a, "attack-melee-right", 1.6);
    else if (f.action === 1 || f.action === 2 || f.action === 5) playClip(a, "holding-right-shoot", 1.5);
    else if (f.action === 4) playClip(a, "interact-right", 1.5);
    else if (f.grounded && f.speed > 6.5) playClip(a, "sprint", f.speed / 7);
    else if (f.grounded && f.speed > 0.4) playClip(a, "walk", Math.max(0.6, f.speed / 3.2));
    else playClip(a, "holding-right");
    if (a.aim) a.aim.rotation.x = -f.pitch * 0.6;
    if (f.status & STATUS_BITS.frozen) for (const g of a.model.anims.values()) if (g.isPlaying) g.speedRatio = 0;
    return;
  }
  a.walkPhase += f.dt * (f.grounded ? f.speed * 2.2 : 0);
  const swing = f.grounded ? Math.sin(a.walkPhase) * Math.min(0.8, f.speed * 0.14) : 0.35;
  a.legL.rotation.x = swing;
  a.legR.rotation.x = f.grounded ? -swing : -0.2;
  a.armL.rotation.x = f.action === 3 ? -1.2 : 0;
}

export function setAvatarVisible(a: Avatar, v: boolean) {
  a.root.setEnabled(v);
}
